import {
  ChevronDownIcon,
  ChevronRightIcon,
  PlusIcon,
  XIcon,
} from '@heroicons/react/outline';
import classNames from 'classnames';
import { NonNegativeNumberInput } from 'components/NonNegativeNumberInput';
import { Toggle } from 'components/Toggle';
import {
  FeeAppliesTo,
  FeeChargeBasis,
  FeeConditionOperator,
  FeeEligibilityScope,
  FeeType,
} from 'types/incentive';
import { DynamicIncentiveFee } from './IncentiveSettings.form';
import {
  doesDynamicFeeMatchSampleSku,
  summarizeDynamicFeeRule,
} from './IncentiveSettings.feeRules';

type EditableFeeField =
  | 'name'
  | 'amount'
  | 'feeType'
  | 'appliesTo'
  | 'enabled'
  | 'chargeBasis';
type DynamicFeeCardProps = {
  fee: DynamicIncentiveFee;
  currency: string;
  nameMaxLength: number;
  isExpanded: boolean;
  sampleSku: string;
  onToggleExpanded: () => void;
  onRemove: () => void;
  onChange: <Field extends EditableFeeField>(
    field: Field,
    value: DynamicIncentiveFee[Field],
  ) => void;
  onScopeChange: (scope: FeeEligibilityScope) => void;
  onAddCondition: () => void;
  onConditionChange: (
    conditionId: string,
    field: 'operator' | 'value',
    value: string,
  ) => void;
  onRemoveCondition: (conditionId: string) => void;
  onSampleSkuChange: (value: string) => void;
};

type DynamicFeeConditionsProps = Pick<
  DynamicFeeCardProps,
  | 'fee'
  | 'sampleSku'
  | 'onAddCondition'
  | 'onConditionChange'
  | 'onRemoveCondition'
  | 'onSampleSkuChange'
>;

const DynamicFeeConditions = ({
  fee,
  sampleSku,
  onAddCondition,
  onConditionChange,
  onRemoveCondition,
  onSampleSkuChange,
}: DynamicFeeConditionsProps) => {
  const hasSampleSku = Boolean(sampleSku.trim());
  const sampleMatches = hasSampleSku
    ? doesDynamicFeeMatchSampleSku(fee, sampleSku)
    : false;
  return (
    <div className="space-y-3 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-gray-700">SKU conditions</p>
          <p className="mt-0.5 text-xs text-gray-500">
            Every condition must match the same returned item.
          </p>
        </div>
        <button
          type="button"
          onClick={onAddCondition}
          className="flex flex-shrink-0 items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
        >
          <PlusIcon className="h-3 w-3" />
          Add SKU condition
        </button>
      </div>

      {fee.eligibility.conditions.map((condition, conditionIndex) => (
        <div
          key={condition.id}
          className="grid gap-2 sm:grid-cols-[180px_minmax(0,1fr)_32px] sm:items-center"
        >
          <select
            aria-label={`SKU condition ${conditionIndex + 1} operator`}
            value={condition.operator}
            onChange={(event) =>
              onConditionChange(
                condition.id,
                'operator',
                event.target.value as FeeConditionOperator,
              )
            }
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
          >
            <option value={FeeConditionOperator.EQUALS}>SKU equals</option>
            <option value={FeeConditionOperator.STARTS_WITH}>
              SKU starts with
            </option>
            <option value={FeeConditionOperator.ENDS_WITH}>
              SKU ends with
            </option>
            <option value={FeeConditionOperator.CONTAINS}>SKU contains</option>
            <option value={FeeConditionOperator.IN}>SKU is one of</option>
          </select>
          <input
            type="text"
            aria-label={`SKU condition ${conditionIndex + 1} value`}
            placeholder={
              condition.operator === FeeConditionOperator.IN
                ? 'SKU-1, SKU-2'
                : 'e.g. -C'
            }
            value={condition.value}
            onChange={(event) =>
              onConditionChange(condition.id, 'value', event.target.value)
            }
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
          />
          <button
            type="button"
            onClick={() => onRemoveCondition(condition.id)}
            aria-label={`Remove SKU condition ${conditionIndex + 1}`}
            className="flex h-8 w-8 items-center justify-center rounded-md text-gray-400 hover:bg-red-50 hover:text-red-500"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>
      ))}

      {fee.eligibility.conditions.length === 0 && (
        <p className="rounded-md border border-dashed border-blue-200 bg-white/60 px-3 py-2 text-xs text-gray-500">
          Add at least one SKU condition.
        </p>
      )}

      <div className="border-t border-blue-100 pt-3">
        <label>
          <span className="mb-1 block text-xs font-medium text-gray-600">
            Test this rule
          </span>
          <input
            type="text"
            placeholder="Try a sample SKU"
            value={sampleSku}
            onChange={(event) => onSampleSkuChange(event.target.value)}
            className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
          />
        </label>
        {hasSampleSku && (
          <p
            className={classNames(
              'mt-2 text-xs font-medium',
              sampleMatches ? 'text-green-700' : 'text-gray-600',
            )}
          >
            {sampleMatches
              ? 'Would apply to this SKU.'
              : 'Would not apply to this SKU.'}
          </p>
        )}
      </div>
    </div>
  );
};

export const DynamicFeeCard = ({
  fee,
  currency,
  nameMaxLength,
  isExpanded,
  sampleSku,
  onToggleExpanded,
  onRemove,
  onChange,
  onScopeChange,
  onAddCondition,
  onConditionChange,
  onRemoveCondition,
  onSampleSkuChange,
}: DynamicFeeCardProps) => (
  <div
    className={classNames(
      'overflow-hidden rounded-lg border border-gray-200 bg-white',
      !fee.enabled && 'opacity-75',
    )}
  >
    <div className="flex items-start gap-3 px-4 py-3">
      <button
        type="button"
        onClick={onToggleExpanded}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Edit'} ${
          fee.name || 'fee rule'
        }`}
        className="mt-0.5 flex min-w-0 flex-1 items-start gap-2 text-left"
      >
        {isExpanded ? (
          <ChevronDownIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
        ) : (
          <ChevronRightIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
        )}
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-gray-900">
            {fee.name || 'Untitled fee rule'}
          </span>
          <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">
            {summarizeDynamicFeeRule(currency, fee)}
          </span>
        </span>
      </button>

      {!fee.enabled && (
        <span className="mt-0.5 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          Paused
        </span>
      )}
      <Toggle
        isSmall
        isEnabled={fee.enabled}
        onChange={(enabled) => onChange('enabled', enabled)}
        ariaLabel={`Enable ${fee.name || 'fee rule'}`}
      />
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove ${fee.name || 'fee'}`}
        className="flex h-7 w-7 items-center justify-center rounded-md text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
      >
        <XIcon className="h-4 w-4" />
      </button>
    </div>

    {isExpanded && (
      <div className="space-y-5 border-t border-gray-200 bg-gray-50 px-4 py-4">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_170px_130px_120px] md:items-end">
          <label>
            <span className="mb-1 block text-xs font-medium text-gray-600">
              Fee name
            </span>
            <input
              type="text"
              placeholder="Restocking fee"
              value={fee.name}
              maxLength={nameMaxLength}
              onChange={(event) => onChange('name', event.target.value)}
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-medium text-gray-600">
              Applies to
            </span>
            <select
              value={fee.appliesTo}
              onChange={(event) =>
                onChange('appliesTo', event.target.value as FeeAppliesTo)
              }
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
            >
              <option value={FeeAppliesTo.REFUND}>Refund only</option>
              <option value={FeeAppliesTo.STORE_CREDIT}>
                Store credit only
              </option>
              <option value={FeeAppliesTo.BOTH}>Refund and store credit</option>
            </select>
          </label>

          <label>
            <span className="mb-1 block text-xs font-medium text-gray-600">
              Fee type
            </span>
            <select
              value={fee.feeType}
              onChange={(event) =>
                onChange('feeType', event.target.value as FeeType)
              }
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
            >
              <option value={FeeType.FIXED}>Fixed</option>
              <option value={FeeType.PERCENTAGE}>Percentage</option>
            </select>
          </label>

          <label>
            <span className="mb-1 block text-xs font-medium text-gray-600">
              Amount
            </span>
            <div className="relative">
              <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 font-bold text-gray-500">
                {fee.feeType === FeeType.PERCENTAGE ? '%' : '$'}
              </span>
              <NonNegativeNumberInput
                placeholder={fee.feeType === FeeType.PERCENTAGE ? '10' : '5.00'}
                value={fee.amount}
                onChange={(event) =>
                  onChange('amount', Number(event.target.value))
                }
                className="block w-full rounded-lg border border-gray-300 bg-white py-2 pl-8 pr-3 text-sm focus:border-black focus:ring-black"
                step={fee.feeType === FeeType.PERCENTAGE ? '1' : '0.01'}
                min="0"
                max={fee.feeType === FeeType.PERCENTAGE ? '100' : undefined}
              />
            </div>
          </label>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <label>
            <span className="mb-1 block text-xs font-medium text-gray-600">
              Apply when
            </span>
            <select
              value={fee.eligibility.scope}
              onChange={(event) =>
                onScopeChange(event.target.value as FeeEligibilityScope)
              }
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
            >
              <option value={FeeEligibilityScope.ALL_RETURNS}>
                All returns
              </option>
              <option value={FeeEligibilityScope.MATCHING_ITEMS}>
                Matching items
              </option>
            </select>
          </label>

          <label>
            <span className="mb-1 block text-xs font-medium text-gray-600">
              Charge frequency
            </span>
            <select
              value={fee.chargeBasis}
              onChange={(event) =>
                onChange('chargeBasis', event.target.value as FeeChargeBasis)
              }
              className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-black focus:ring-black"
            >
              <option value={FeeChargeBasis.PER_RETURN}>Once per return</option>
              <option
                value={FeeChargeBasis.PER_MATCHING_UNIT}
                disabled={
                  fee.eligibility.scope === FeeEligibilityScope.ALL_RETURNS
                }
              >
                Per matching unit
              </option>
            </select>
          </label>
        </div>

        {fee.eligibility.scope === FeeEligibilityScope.MATCHING_ITEMS && (
          <DynamicFeeConditions
            fee={fee}
            sampleSku={sampleSku}
            onAddCondition={onAddCondition}
            onConditionChange={onConditionChange}
            onRemoveCondition={onRemoveCondition}
            onSampleSkuChange={onSampleSkuChange}
          />
        )}
      </div>
    )}
  </div>
);
